import {HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse} from "@angular/common/http";
import {inject} from "@angular/core";
import {Observable, Subscriber} from "rxjs";
import dayjs from "dayjs/esm";

import {HttpCacheService} from "./http-cache.service";
import {CacheSettings, HTTP_CACHE_ENABLED, HTTP_CACHE_SETTINGS} from "./http-context";
import {__} from "@app/shared/functions/object.functions";

export {CacheSettings} from "./http-context";

const urlExclusionPattern =
  /\/api\/v1\/files\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\.\w+\/urls/;

export function normalizeCacheSettings(options?: CacheSettings | null): {settings?: CacheSettings; forceUpdate: boolean} {
  if (__.IsNullOrUndefined(options)) {
    return {forceUpdate: false};
  }

  const settings = Object.assign(new CacheSettings(), options);
  if (!__.IsNullOrUndefined(settings.expires) && !(settings.expires instanceof Date)) {
    settings.expires = dayjs()
      .add(settings.expires as number, "second")
      .toDate();
  }

  return {
    settings,
    forceUpdate: !__.IsNullOrUndefined(settings.update) ? settings.update : false,
  };
}

export function handleCachedRequest(
  request: HttpRequest<any>,
  next: (request: HttpRequest<any>) => Observable<HttpEvent<any>>,
  httpCacheService: HttpCacheService,
  settings?: CacheSettings,
  forceUpdate = false,
): Observable<HttpEvent<any>> {
  if (request.method !== "GET" && !urlExclusionPattern.test(request.url)) {
    return next(request);
  }

  return new Observable((subscriber: Subscriber<HttpEvent<any>>) => {
    const cachedData = forceUpdate ? null : httpCacheService.getCacheData(request.urlWithParams);
    if (cachedData !== null) {
      subscriber.next(new HttpResponse(cachedData as Object));
      subscriber.complete();
      return;
    }

    next(request).subscribe({
      next: (event) => {
        if (event instanceof HttpResponse) {
          httpCacheService.setCacheData(
            request.urlWithParams,
            event,
            settings?.lastUpdated,
            settings?.expires as Date,
          );
        }
        subscriber.next(event);
      },
      error: (error) => subscriber.error(error),
      complete: () => subscriber.complete(),
    });
  });
}

export const cacheInterceptorFn: HttpInterceptorFn = (request, next) => {
  if (!request.context.get(HTTP_CACHE_ENABLED)) {
    return next(request);
  }

  const httpCacheService = inject(HttpCacheService);
  httpCacheService.setPersistence("local");
  const {settings, forceUpdate} = normalizeCacheSettings(request.context.get(HTTP_CACHE_SETTINGS));
  return handleCachedRequest(request, next, httpCacheService, settings, forceUpdate);
};
